import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Typography,
  TextField,
  useMediaQuery,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DateRangeIcon from "@mui/icons-material/DateRange";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TodayIcon from "@mui/icons-material/Today";
import { useDispatch } from "react-redux";
import AttendanceVeiwController from "../../redux/actions/AttendenceApiAction";
import dayjs from "dayjs";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MONTHS = [
  { label: "Jan", value: 0 },
  { label: "Feb", value: 1 },
  { label: "Mar", value: 2 },
  { label: "Apr", value: 3 },
  { label: "May", value: 4 },
  { label: "Jun", value: 5 },
  { label: "Jul", value: 6 },
  { label: "Aug", value: 7 },
  { label: "Sep", value: 8 },
  { label: "Oct", value: 9 },
  { label: "Nov", value: 10 },
  { label: "Dec", value: 11 },
];

const ReportMonthPicker = ({ selectedMonth, onChange }) => {
  const year = selectedMonth.year();
  const monthIndex = selectedMonth.month();
  const now = dayjs();

  const setYear = (nextYear) => {
    onChange(selectedMonth.year(nextYear));
  };

  const setMonth = (index) => {
    onChange(selectedMonth.month(index));
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mb: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setYear(year - 1)}
          aria-label="Previous year"
          sx={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: "10px",
            width: 32,
            height: 32,
            color: BRAND.navy,
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: BRAND.navy,
            minWidth: 56,
            textAlign: "center",
          }}
        >
          {year}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setYear(year + 1)}
          aria-label="Next year"
          sx={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: "10px",
            width: 32,
            height: 32,
            color: BRAND.navy,
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 0.75,
        }}
      >
        {MONTHS.map(({ label, value }) => {
          const isSelected = monthIndex === value;
          const isToday = now.year() === year && now.month() === value;

          return (
            <ButtonBase
              key={value}
              onClick={() => setMonth(value)}
              sx={{
                py: 1,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: isSelected ? "#fff" : BRAND.navy,
                bgcolor: isSelected ? BRAND.orangeHover : "#fff",
                border: `1px solid ${
                  isSelected ? BRAND.orangeHover : isToday ? BRAND.orangeHover : BRAND.border
                }`,
                boxShadow: isSelected ? "0 2px 8px rgba(230, 90, 0, 0.25)" : "none",
                transition: "background-color 0.15s, border-color 0.15s, color 0.15s",
                "&:active": {
                  transform: "scale(0.98)",
                },
              }}
            >
              {label}
            </ButtonBase>
          );
        })}
      </Box>

      <Box
        sx={{
          mt: 1.5,
          py: 0.75,
          px: 1.25,
          borderRadius: "8px",
          bgcolor: BRAND.orangeLight,
          border: `1px solid ${BRAND.orangeHover}33`,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted }}>
          Report Period
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: BRAND.navy }}>
          {selectedMonth.format("MMMM YYYY")}
        </Typography>
      </Box>
    </Box>
  );
};

const ReportDateRangePicker = ({ startDate, endDate, setStartDate, setEndDate }) => {
  const todayStr = dayjs().format("YYYY-MM-DD");
  const monthStartStr = dayjs().startOf("month").format("YYYY-MM-DD");
  const monthEndStr = dayjs().endOf("month").format("YYYY-MM-DD");

  const handleQuickToday = () => {
    setStartDate(todayStr);
    setEndDate(todayStr);
  };

  const handleQuickThisMonth = () => {
    setStartDate(monthStartStr);
    setEndDate(monthEndStr);
  };

  return (
    <Box sx={{ pt: 0.5 }}>
      {/* Quick selection chips */}
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Chip
          icon={<TodayIcon style={{ fontSize: "0.95rem" }} />}
          label="Today (Current Date)"
          onClick={handleQuickToday}
          size="small"
          variant={startDate === todayStr && endDate === todayStr ? "filled" : "outlined"}
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor: startDate === todayStr && endDate === todayStr ? BRAND.orangeHover : "transparent",
            color: startDate === todayStr && endDate === todayStr ? "#fff" : BRAND.navy,
            borderColor: BRAND.orangeHover,
            "& .MuiChip-icon": {
              color: startDate === todayStr && endDate === todayStr ? "#fff" : BRAND.orangeHover,
            },
          }}
        />
        <Chip
          icon={<CalendarMonthIcon style={{ fontSize: "0.95rem" }} />}
          label="This Month"
          onClick={handleQuickThisMonth}
          size="small"
          variant={startDate === monthStartStr && endDate === monthEndStr ? "filled" : "outlined"}
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            bgcolor: startDate === monthStartStr && endDate === monthEndStr ? BRAND.orangeHover : "transparent",
            color: startDate === monthStartStr && endDate === monthEndStr ? "#fff" : BRAND.navy,
            borderColor: BRAND.orangeHover,
            "& .MuiChip-icon": {
              color: startDate === monthStartStr && endDate === monthEndStr ? "#fff" : BRAND.orangeHover,
            },
          }}
        />
      </Box>

      {/* Date inputs */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: endDate }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: startDate }}
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          py: 0.75,
          px: 1.25,
          borderRadius: "8px",
          bgcolor: BRAND.orangeLight,
          border: `1px solid ${BRAND.orangeHover}33`,
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted }}>
          Selected Period
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: BRAND.navy }}>
          {startDate === endDate
            ? dayjs(startDate).format("DD MMMM YYYY (Single Day)")
            : `${dayjs(startDate).format("DD MMM YYYY")} → ${dayjs(endDate).format("DD MMM YYYY")}`}
        </Typography>
      </Box>
    </Box>
  );
};

const SheetHandle = () => (
  <Box sx={{ display: "flex", justifyContent: "center", pt: 1.25, pb: 0.5 }}>
    <Box
      sx={{
        width: 40,
        height: 4,
        borderRadius: 2,
        bgcolor: BRAND.border,
      }}
    />
  </Box>
);

const ReportSheetHeader = ({ employeeCount }) => (
  <Box sx={{ px: 2, pb: 0.5 }}>
    <Typography sx={{ fontWeight: 700, color: BRAND.navy, fontSize: "1.05rem" }}>
      Generate Attendance Report
    </Typography>
    <Typography sx={{ fontSize: "0.78rem", color: BRAND.muted, mt: 0.25 }}>
      Export records for {employeeCount} employee{employeeCount > 1 ? "s" : ""}
    </Typography>
  </Box>
);

const AddReportMonth = ({
  open,
  anchorRef,
  handleClose,
  selectedIds,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { genrateAttendanceReport } = AttendanceVeiwController();

  const [mode, setMode] = useState("range"); // "range" | "month"
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const popperRef = useRef(null);

  const handleGenerate = () => {
    let payload = {
      employee_ids: selectedIds,
    };

    if (mode === "range") {
      payload.start_date = startDate;
      payload.end_date = endDate;
    } else {
      payload.month = selectedMonth.format("YYYY-MM");
    }

    dispatch(genrateAttendanceReport(payload));
    handleClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  useEffect(() => {
    if (isMobile || !open) return undefined;

    const handleClickOutside = (e) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClose, anchorRef, isMobile, open]);

  if (!open) return null;

  const content = (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={mode}
        onChange={(e, val) => setMode(val)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          borderBottom: `1px solid ${BRAND.border}`,
          px: 2,
          mb: 1.5,
          "& .MuiTab-root": {
            minHeight: 36,
            py: 0.5,
            fontSize: "0.78rem",
            fontWeight: 600,
            textTransform: "none",
          },
          "& .Mui-selected": {
            color: BRAND.orangeHover,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: BRAND.orangeHover,
          },
        }}
      >
        <Tab icon={<DateRangeIcon style={{ fontSize: "0.95rem" }} />} iconPosition="start" label="Date Range" value="range" />
        <Tab icon={<CalendarMonthIcon style={{ fontSize: "0.95rem" }} />} iconPosition="start" label="Month View" value="month" />
      </Tabs>

      <Box sx={{ px: 2, pb: 1 }}>
        {mode === "range" ? (
          <ReportDateRangePicker
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        ) : (
          <ReportMonthPicker
            selectedMonth={selectedMonth}
            onChange={setSelectedMonth}
          />
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="xs"
        sx={{
          "& .MuiDialog-container": { alignItems: "flex-end" },
        }}
        PaperProps={{
          sx: {
            m: 0,
            width: "100%",
            maxWidth: "100%",
            borderRadius: "16px 16px 0 0",
            pb: "max(env(safe-area-inset-bottom), 8px)",
          },
        }}
      >
        <SheetHandle />
        <ReportSheetHeader employeeCount={selectedIds.length} />
        <DialogContent sx={{ px: 0, pt: 0.5, pb: 1 }}>
          {content}
        </DialogContent>
        <Box
          sx={{
            width: "100%",
            borderTop: `1px solid ${BRAND.border}`,
            bgcolor: "#fff",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Button
            onClick={handleGenerate}
            variant="contained"
            fullWidth
            sx={{
              ...primaryButtonSx,
              minHeight: 44,
              borderRadius: "12px",
            }}
          >
            Generate Excel Report
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            fullWidth
            sx={{
              minHeight: 40,
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    );
  }

  if (!anchorRef?.current) return null;
  const anchorRect = anchorRef.current.getBoundingClientRect();

  return (
    <div
      ref={popperRef}
      style={{
        position: "absolute",
        top: anchorRect.bottom + window.scrollY + 8,
        left: Math.min(
          anchorRect.left + window.scrollX,
          window.scrollX + window.innerWidth - 340
        ),
        background: "#fff",
        border: `1px solid ${BRAND.border}`,
        borderRadius: 12,
        boxShadow: "0 8px 24px rgba(26, 43, 74, 0.14)",
        zIndex: 1050,
        width: 320,
        overflow: "hidden",
      }}
    >
      <Box sx={{ pt: 1.5 }}>
        <ReportSheetHeader employeeCount={selectedIds.length} />
      </Box>

      {content}

      <Box
        sx={{
          borderTop: `1px solid ${BRAND.border}`,
          px: 2,
          py: 1.25,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          bgcolor: "#fff",
        }}
      >
        <Button size="small" onClick={handleClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button size="small" onClick={handleGenerate} variant="contained" sx={primaryButtonSx}>
          Generate Report
        </Button>
      </Box>
    </div>
  );
};

export default AddReportMonth;
